import {
  Banner,
  Button,
  Checkbox,
  FlexBox,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  Spinner,
  Text,
} from '@dixa/lib-shared-habla';
import { client as elevioClient } from 'elevio';
import { ampli } from 'lib-shared-tracking';
import { useCallback, useState } from 'react';
import { useMount } from 'react-use';

import { queuesApi } from 'apis/queues';
import { searchApi } from 'apis/search';
import { AppLink } from 'components/appLink';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { articles } from 'utils/elevio';
import { createAppModal } from 'utils/modal';

import styles from './queueArchiveModal.module.scss';

export const QueueArchiveModal = createAppModal(
  { type: 'settings:queueArchive' },
  ({
    isOpen,
    modal: {
      arg: { queue, goToArchivedQueues },
    },
    meta,
    hide,
  }): JSX.Element => {
    const notify = useNotifications();
    const [isConfirmed, setIsConfirmed] = useState(queue.is_archived);

    const [updateQueue, { isLoading: isUpdating }] =
      queuesApi.useUpdateQueueMutation();

    const [
      readConversations,
      { openConversationsCount, hasConversations, isLoadingConversations },
    ] = searchApi.useLazyReadAdvancedSearchConversationsIdsBulkQuery({
      selectFromResult: ({ data, isFetching }) => ({
        isLoadingConversations: isFetching,
        openConversationsCount: data?.data.total_conversations,
        hasConversations: (data?.data.total_conversations ?? 0) > 0,
      }),
    });

    useMount(() => {
      if (queue.is_archived) return;

      readConversations({
        type: 'conversations',
        params: {
          filters: {
            strategy: 'all',
            conditions: [
              {
                field: 'queue_id',
                operator: 'is_one',
                values: [{ label: queue.name, value: queue.id }],
              },
              {
                field: 'status',
                operator: 'is_one',
                values: [
                  { label: '', value: 'open' },
                  { label: '', value: 'pending' },
                ],
              },
            ],
          },
        },
      });
    });

    const handleConfirm = useCallback(async () => {
      try {
        const nextIsArchived = !queue.is_archived;

        const { data: updatedQueue } = await updateQueue({
          id: queue.id,
          queue: { is_archived: nextIsArchived },
        }).unwrap();

        if (nextIsArchived) {
          ampli.settingsQueuesArchiveQueueClicked({
            openConversationsCount,
          });
        } else {
          ampli.settingsQueuesUnarchiveQueueClicked();
        }

        notify(
          {
            title: queue.is_archived ? 'Queue unarchived' : 'Queue archived',
            text: `"${queue.name}" has been ${
              queue.is_archived ? 'unarchived' : 'archived'
            }.`,
            icon: 'i-done',
            time: 5000,
            leftAction: {
              text: 'See all archived queues',
              action: goToArchivedQueues,
            },
          },
          NotificationType.Success,
        );

        meta?.resolve({ updatedQueue });
        hide?.();
      } catch (error) {
        notify(
          {
            title: 'Something went wrong while updating the queue.',
          },
          NotificationType.Error,
        );
      }
    }, [
      goToArchivedQueues,
      hide,
      meta,
      notify,
      openConversationsCount,
      queue,
      updateQueue,
    ]);

    const renderArchiveContent = useCallback(() => {
      const conversationsLink = (
        <AppLink
          path="/conversations"
          params={{
            search: { queue: [queue.id], status: ['open', 'pending'] },
          }}
          target="_blank"
        >
          {openConversationsCount} open{' '}
          {openConversationsCount === 1 ? 'conversation' : 'conversations'}
        </AppLink>
      );

      return (
        <>
          {isLoadingConversations && (
            <FlexBox
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              gap="300"
            >
              <Spinner />

              <Text.Paragraph>Checking open conversations...</Text.Paragraph>
            </FlexBox>
          )}

          {!isLoadingConversations && (
            <FlexBox gap="400" flexDirection="column">
              <Banner
                variant="warning"
                description={
                  <Text.Paragraph>
                    Please unassign this queue from all flows and third-party
                    integrations where this queue is referenced before archiving
                    it.
                  </Text.Paragraph>
                }
                actionSlot={
                  <Button
                    variant="secondary"
                    onClick={() => {
                      elevioClient.openArticle(
                        articles.settings.queues.archiving,
                      );
                    }}
                  >
                    Read more
                  </Button>
                }
                icon={null}
              />

              <Text.Paragraph>
                This queue will no longer be visible as an option in various
                selectors and dashboards.
              </Text.Paragraph>

              {hasConversations && (
                <Text.Paragraph>
                  Please be aware that this queue has {conversationsLink}. We
                  suggest to close or transfer these conversations out of this
                  queue before archiving.
                </Text.Paragraph>
              )}

              <FlexBox as="label" alignItems="center" gap="300">
                <Checkbox
                  name="confirmQueueArchive"
                  checked={isConfirmed}
                  onChange={() =>
                    setIsConfirmed((isCheckboxChecked) => !isCheckboxChecked)
                  }
                />

                <Text.Paragraph>
                  {hasConversations
                    ? 'I am aware that this queue has open active conversations that will remain in this queue and might still receive new conversations if it was not unassigned from its respective flows.'
                    : 'I am aware that this queue might still receive new conversations if it was not unassigned from its respective flows.'}
                </Text.Paragraph>
              </FlexBox>
            </FlexBox>
          )}
        </>
      );
    }, [
      hasConversations,
      isConfirmed,
      isLoadingConversations,
      openConversationsCount,
      queue.id,
    ]);

    const renderUnarchiveContent = useCallback(() => {
      return (
        <Text.Paragraph>
          Are you sure you want to unarchive this queue?
        </Text.Paragraph>
      );
    }, []);

    return (
      <Modal isOpen={isOpen} onClose={hide} className={styles.modal}>
        <ModalHead
          title={`${queue.is_archived ? 'Unarchive' : 'Archive'} "${
            queue.name
          }"`}
          onClose={hide}
        />

        <ModalBody>
          {queue.is_archived
            ? renderUnarchiveContent()
            : renderArchiveContent()}
        </ModalBody>

        <ModalFoot
          cancelOnClick={hide}
          confirmName={queue.is_archived ? 'Unarchive' : 'Archive'}
          confirmOnClick={handleConfirm}
          confirmProps={{
            type: 'submit',
            isLoading: isUpdating,
            disabled: !isConfirmed || isUpdating,
          }}
        />
      </Modal>
    );
  },
);
